// Login del inspector: email/password + partner editable. Ejercita el flujo
// OAuth REAL (auth code + PKCE) — si esto funciona, la integración de la OTT
// contra /auth/login y /oauth/token también.

import { useState, type FormEvent } from 'react';
import { emailInputProps, normalizeEmail } from '@/lib/email-normalize';
import { useLocation, useNavigate } from 'react-router-dom';
import { Activity, KeyRound, ShieldAlert } from 'lucide-react';
import {
  completeConsent,
  DEFAULT_PARTNER_ID,
  InspectorApiError,
  isConsentRequired,
  loginInspector,
  type ConsentRequired,
} from '@/lib/api';
import { useI18n, type TFn } from '@/lib/i18n';
import { LangSwitch } from '@/components/LangSwitch';

const KNOWN_ERRORS = [
  'invalid_credentials',
  'user_blocked',
  'email_not_verified',
  'rate_limited',
  'network_error',
  'partner_suspended',
];

function errorCopy(t: TFn, err: unknown): string {
  const code = err instanceof InspectorApiError ? err.code : 'unknown';
  if (KNOWN_ERRORS.includes(code)) return t(`err.${code}`);
  return t('login.errGeneric', {
    code,
    msg: err instanceof Error ? err.message : 'unknown error',
  });
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const from = (location.state as { from?: string } | null)?.from ?? '/panel';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [partnerId, setPartnerId] = useState(DEFAULT_PARTNER_ID);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [consent, setConsent] = useState<ConsentRequired | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMsg(null);
    try {
      const result = await loginInspector({ email: normalizeEmail(email), password, partnerId: partnerId.trim() });
      if (isConsentRequired(result)) {
        setConsent(result);
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setErrorMsg(errorCopy(t, err));
    } finally {
      setBusy(false);
    }
  }

  async function handleConsentAccept() {
    if (!consent) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      await completeConsent(consent.consent_ticket);
      navigate(from, { replace: true });
    } catch (err) {
      setConsent(null);
      const code = err instanceof InspectorApiError ? err.code : 'unknown';
      setErrorMsg(t('login.errConsent', { code }));
    } finally {
      setBusy(false);
    }
  }

  const inputClass =
    'w-full rounded border border-surface-border-strong bg-surface-muted px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-brand focus:outline-none focus-visible:ring-1 focus-visible:ring-brand';

  return (
    <main id="main" className="flex min-h-screen items-center justify-center p-4">
      <div id="login-card" className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <p className="flex items-center justify-center gap-2 text-lg font-bold tracking-[0.25em] text-brand">
            <Activity className="h-5 w-5" aria-hidden="true" />
            AUTHSYS//INSPECTOR
          </p>
          <p className="mt-2 text-[11px] uppercase tracking-widest text-status-warning">
            {t('login.tagline')}
          </p>
          <div className="mt-4 flex justify-center">
            <LangSwitch />
          </div>
        </div>

        <div className="rounded border border-surface-border bg-surface p-5">
          {consent ? (
            <div className="space-y-4 text-sm">
              <p className="flex items-center gap-2 font-semibold text-status-warning">
                <ShieldAlert className="h-4 w-4" aria-hidden="true" />
                {t('login.consentTitle')}
              </p>
              <p className="text-[12px] leading-relaxed text-ink-muted">
                {t('login.consentBody', { version: consent.tos.version })}
              </p>
              {consent.tos.tos_url && (
                <a
                  href={consent.tos.tos_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-[12px] text-brand underline-offset-4 hover:underline"
                >
                  {t('login.consentLink', { version: consent.tos.version })}
                </a>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleConsentAccept}
                  disabled={busy}
                  className="min-h-[2.5rem] flex-1 rounded bg-brand font-semibold text-surface-muted hover:bg-brand-hover disabled:opacity-50"
                >
                  {busy ? t('login.consentAccepting') : t('login.consentAccept')}
                </button>
                <button
                  type="button"
                  onClick={() => setConsent(null)}
                  disabled={busy}
                  className="min-h-[2.5rem] rounded border border-surface-border px-3 text-ink-muted hover:border-surface-border-strong"
                >
                  {t('login.consentCancel')}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1 block text-[10px] uppercase tracking-widest text-ink-subtle">
                  {t('login.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  {...emailInputProps(setEmail)}
                  className={inputClass}
                  placeholder={t('login.emailPlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="password" className="mb-1 block text-[10px] uppercase tracking-widest text-ink-subtle">
                  {t('login.password')}
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="partner" className="mb-1 block text-[10px] uppercase tracking-widest text-ink-subtle">
                  {t('login.partner')}
                </label>
                <input
                  id="partner"
                  type="text"
                  required
                  value={partnerId}
                  onChange={(e) => setPartnerId(e.target.value)}
                  className={inputClass}
                  spellCheck={false}
                />
              </div>

              {errorMsg && (
                <p role="alert" className="rounded border border-status-error/40 bg-status-error/10 px-3 py-2 text-[12px] text-status-error">
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                className="flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded bg-brand text-sm font-semibold text-surface-muted hover:bg-brand-hover focus-visible:ring-2 focus-visible:ring-brand-hover disabled:opacity-50"
              >
                <KeyRound className="h-4 w-4" aria-hidden="true" />
                {busy ? t('login.submitting') : t('login.submit')}
              </button>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-[11px] leading-relaxed text-ink-subtle">
          {t('login.hint')}
        </p>
      </div>
    </main>
  );
}
